import type { UseFormReturn } from "react-hook-form";
import { Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import type { ThemeClasses } from "../types";
import type { Device } from "../../../../../../store/admin/devicesStore";
import type { User } from "../../../../../../store/admin/usersStore";
import { getCustomSelectStyles } from "../../../../shared";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";

export interface GroupFormData {
  name: string;
  description?: string;
  status_id: string;
  devices_ids: string[];
  monitorista_ids: string[];
}

interface GroupFormProps {
  form: UseFormReturn<GroupFormData>;
  isEdit: boolean;
  loadingSubmit: boolean;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  devices: Device[];
  devicesLoading?: boolean;
  devicesInitialLoading?: boolean;
  onDevicesMenuScrollToBottom?: () => void;
  monitoristas: User[];
  monitoristasLoading?: boolean;
  onSubmit: (data: GroupFormData) => void;
  onCancel: () => void;
}

export function GroupForm({
  form,
  isEdit,
  loadingSubmit,
  themeClasses,
  isDarkMode,
  devices,
  devicesLoading,
  devicesInitialLoading,
  onDevicesMenuScrollToBottom,
  monitoristas,
  monitoristasLoading,
  onSubmit,
  onCancel,
}: GroupFormProps) {
  const { t } = useTranslation("adminSections");
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const inputClassName = `w-full text-sm px-4 py-3 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`;

  const deviceOptions = devices.map((device) => {
    const identifier = device.identifier || device.id;
    const label = device.vehicle?.name
      ? `${identifier} (${device.vehicle.name})`
      : identifier;

    return {
      value: device.id,
      label,
    };
  });

  // El estilo compartido fija menuList a 400px, lo que impide que react-select
  // ajuste el menú al espacio disponible y la lista se sale de la pantalla.
  // Respetamos la altura calculada (state.maxHeight) para que quepa siempre.
  const multiSelectStyles = {
    ...getCustomSelectStyles(isDarkMode),
    menuList: (base: any, state: any) => ({
      ...base,
      padding: "0.5rem",
      maxHeight: state.maxHeight,
    }),
  };

  const monitoristaOptions = monitoristas.map((user) => {
    const fullName = `${user.first_name} ${user.last_name}`;
    return {
      value: user.id,
      label: `${fullName} (${user.email})`,
    };
  });

  const statuses = useStatusesStore((state) => state.statuses);
  const statusOptionsSelect = getStatusOptionsForEntity(statuses, "groups", t);

  // Seleccionar/deseleccionar todos los dispositivos cargados
  const allDeviceIds = deviceOptions.map((opt) => opt.value);
  const selectedDeviceIds = form.watch("devices_ids") || [];
  const allDevicesSelected =
    allDeviceIds.length > 0 &&
    allDeviceIds.every((id) => selectedDeviceIds.includes(id));
  const handleToggleAllDevices = () => {
    form.setValue("devices_ids", allDevicesSelected ? [] : allDeviceIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <form
      className="flex flex-col w-full gap-5"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Nombre del Grupo */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("groups.fields.name")} <span className="text-red-500">*</span>
        </label>
        <input
          {...register("name", { required: true })}
          type="text"
          className={`${inputClassName} ${
            errors.name ? "ring-2 ring-red-500" : ""
          }`}
          placeholder={t("groups.form.namePlaceholder")}
        />
        {errors.name && (
          <span className="text-red-500 text-xs">
            {t("groups.form.requiredField")}
          </span>
        )}
      </div>

      {/* Descripción */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("groups.fields.description")}
        </label>
        <textarea
          {...register("description")}
          className={`${inputClassName} resize-none`}
          rows={2}
          placeholder={t("groups.form.descriptionPlaceholder")}
        />
      </div>

      {/* Estado */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("groups.fields.status")} <span className="text-red-500">*</span>
        </label>
        <RHFController
          name="status_id"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              {...field}
              options={statusOptionsSelect}
              styles={getCustomSelectStyles(isDarkMode)}
              placeholder={t("groups.form.selectStatus")}
              value={statusOptionsSelect.find((opt: any) => opt.value === field.value)}
              onChange={(option: any) => field.onChange(option?.value)}
              isSearchable
              noOptionsMessage={() => t("groups.form.noStatusesAvailable")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          )}
        />
        {errors.status_id && (
          <span className="text-red-500 text-xs">
            {t("groups.form.requiredField")}
          </span>
        )}
      </div>

      {/* Dispositivos */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <label className={`text-sm font-medium ${themeClasses.text}`}>
            {t("groups.fields.devices")}
            {selectedDeviceIds.length > 0 && ` (${selectedDeviceIds.length})`}
          </label>
          {allDeviceIds.length > 0 && (
            <button
              type="button"
              onClick={handleToggleAllDevices}
              className={`text-xs font-medium px-2.5 py-1 rounded-md bg-primary-500/10 text-primary-500 hover:bg-primary-500/20 border border-primary-500/20 transition-colors`}
            >
              {allDevicesSelected
                ? t("groups.form.deselectAllDevices", "Deseleccionar todos")
                : `${t("groups.form.selectAllDevices", "Seleccionar todos")} (${allDeviceIds.length})`}
            </button>
          )}
        </div>
        {devicesInitialLoading ? (
          <div className={`flex items-center justify-center py-3 px-4 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-50"
          } border ${themeClasses.cardBorder} rounded-lg`}>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-2"></div>
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {t("groups.form.loadingDevices", "Cargando dispositivos...")}
            </span>
          </div>
        ) : (
          <RHFController
            name="devices_ids"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={deviceOptions}
                styles={multiSelectStyles as any}
                placeholder={t("groups.form.selectDevices")}
                value={(field.value || []).map(
                  (id) =>
                    deviceOptions.find((opt) => opt.value === id) || {
                      value: id,
                      label: id,
                    }
                )}
                onChange={(options) =>
                  field.onChange(options?.map((opt: any) => opt.value) || [])
                }
                isSearchable
                isLoading={devicesLoading}
                loadingMessage={() => t("groups.form.loadingMoreDevices", "Cargando más dispositivos...")}
                onMenuScrollToBottom={onDevicesMenuScrollToBottom}
                noOptionsMessage={() => t("groups.form.noDevicesAvailable")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
              />
            )}
          />
        )}
        <span className={`text-xs ${themeClasses.textSecondary}`}>
          {t("groups.form.devicesHelp")}
        </span>
      </div>

      {/* Monitoristas */}
      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${themeClasses.text}`}>
          {t("groups.fields.monitoristas", "Monitoristas")}
        </label>
        {monitoristasLoading ? (
          <div className={`flex items-center justify-center py-3 px-4 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-50"
          } border ${themeClasses.cardBorder} rounded-lg`}>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-2"></div>
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {t("groups.form.loadingMonitoristas", "Cargando monitoristas...")}
            </span>
          </div>
        ) : (
          <RHFController
            name="monitorista_ids"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={monitoristaOptions}
                styles={multiSelectStyles as any}
                placeholder={t("groups.form.selectMonitoristas", "Seleccionar monitoristas")}
                value={(field.value || []).map(
                  (id) =>
                    monitoristaOptions.find((opt) => opt.value === id) || {
                      value: id,
                      label: id,
                    }
                )}
                onChange={(options) =>
                  field.onChange(options?.map((opt: any) => opt.value) || [])
                }
                isSearchable
                noOptionsMessage={() => t("groups.form.noMonitoristasAvailable", "No hay monitoristas disponibles")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                maxMenuHeight={250}
              />
            )}
          />
        )}
        <span className={`text-xs ${themeClasses.textSecondary}`}>
          {t("groups.form.monitoristasHelp", "Solo usuarios con rol de monitorista")}
        </span>
      </div>

      {/* Botones */}
      <div className="flex gap-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className={`flex-1 py-3 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
        >
          {t("groups.actions.cancel")}
        </button>
        <button
          type="submit"
          disabled={!isValid || loadingSubmit}
          className={`flex-1 py-3 px-4 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg font-medium transition-colors ${
            !isValid || loadingSubmit ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {loadingSubmit
            ? isEdit
              ? t("groups.form.submitting")
              : t("groups.form.creating")
            : isEdit
            ? t("groups.actions.saveChanges")
            : t("groups.actions.create")}
        </button>
      </div>
    </form>
  );
}
